import { formatLongDate } from '@/lib/format-long-date';
import { Badge, HStack, Text } from '@chakra-ui/react';
import type { DisplayRightsProposalSummary } from '@fluenceur/types';
import { Calendar } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { resolveDisplayRightsExpiry } from './display-rights-status';

interface DisplayRightsExpiryLineProps {
  proposal: DisplayRightsProposalSummary | null;
}

/**
 * When the display rights stop covering ads, and how close that is.
 *
 * Derived entirely from the summary the surface already holds — no request of
 * its own — and rendered on every surface that states the compensation, because
 * a deadline that only appears in one place is a deadline half the team never
 * sees.
 *
 * Renders nothing while the rights have not started: their term begins at the
 * first launch, and announcing a date before then would promise one the
 * agreement does not have yet.
 */
export function DisplayRightsExpiryLine({ proposal }: DisplayRightsExpiryLineProps) {
  const { t, i18n } = useTranslation();
  const expiry = resolveDisplayRightsExpiry(proposal);

  if (expiry.phase === 'none' || !expiry.endAt) return null;

  const date = formatLongDate(expiry.endAt, i18n.language);

  return (
    <HStack gap="1.5" minW="0" data-testid="display-rights-expiry-line" data-phase={expiry.phase}>
      <Calendar width={12} height={12} color="currentColor" />
      <Text fontSize="11px" color="primary.500">
        {expiry.phase === 'ended'
          ? t('displayRights.expiry.ended', { date })
          : t('displayRights.expiry.until', { date })}
      </Text>
      {expiry.phase === 'approaching' && (
        <Badge
          colorPalette="orange"
          variant="subtle"
          size="xs"
          textTransform="none"
          data-testid="display-rights-expiry-badge"
        >
          {t('displayRights.expiry.daysLeft', { count: expiry.daysLeft ?? 0 })}
        </Badge>
      )}
    </HStack>
  );
}
